/**
 * Text Motion Engine (template_engine editor preview)
 * =====================================================
 *
 * A dedicated, self-contained motion system for text items, separate from the
 * animation engine (animationEngine.js). Motion describes how a text layer
 * MOVES over time (position / scale / rotation / 3D / path / spacing), and is
 * the base transform onto which entrance/emphasis/exit animations can layer.
 *
 * This file is mirrored 1:1 by the Python worker (motion_engine.py). Keep the
 * math IDENTICAL so the editor preview matches the rendered video.
 *
 * ---------------------------------------------------------------------------
 * Data model — item.motion
 * ---------------------------------------------------------------------------
 * A text item carries an optional `motion` object:
 *   {
 *     type: 'positionMotion' | 'scaleMotion' | ... ,  // preset id
 *     start: number,        // seconds, relative to item.time.start
 *     duration: number,     // seconds
 *     easing: 'linear'|'easeIn'|'easeOut'|'easeInOut'|'spring'|'bezier',
 *     bezier?: [x1,y1,x2,y2],   // when easing === 'bezier'
 *     loop?: { mode:'none'|'repeat'|'reverse'|'pingpong', count?:number },
 *     speed?: { graph: [[t,v],...] },  // optional time-remap (progress curve)
 *     constrain?: { minX,maxX,minY,maxY, axis:'none'|'x'|'y' },
 *     perChar?: { enabled, stagger, mode:'char'|'word', seed },
 *     ...preset-specific params
 *   }
 *
 * computeMotion(motionConfig, t, itemTime, ctx) -> MotionResult
 *   t       = absolute current time (seconds)
 *   itemTime = { start, duration } of the item
 *   ctx     = { width, height, fontSize, text, lineCount } composition context
 *
 * MotionResult (all optional; identity defaults applied by the renderer):
 *   { x, y, scaleX, scaleY, rotation, rotationX, rotationY,
 *     opacity, letterSpacing, lineSpacing, blur, anchorX, anchorY,
 *     perChar: [{x,y,scaleX,scaleY,rotation,rotationX,rotationY,opacity}],
 *     motionBlur: { angle, amount } }
 */

// ---------------------------------------------------------------------------
// Identity result
// ---------------------------------------------------------------------------

export const IDENTITY_MOTION = Object.freeze({
  x: 0, y: 0,
  scaleX: 1, scaleY: 1,
  rotation: 0, rotationX: 0, rotationY: 0,
  opacity: 1,
  letterSpacing: 0, lineSpacing: 0,
  blur: 0,
  anchorX: 0.5, anchorY: 0.5,
  perChar: null,
  motionBlur: null,
});

const identity = () => ({ ...IDENTITY_MOTION });

// ---------------------------------------------------------------------------
// Easing
// ---------------------------------------------------------------------------

/** Clamp to [0,1]. */
const clamp01 = (v) => Math.max(0, Math.min(1, v));

/** Cubic-bezier easing via Newton iteration (matches CSS cubic-bezier). */
const cubicBezierEase = (p, x1, y1, x2, y2) => {
  if (p <= 0) return 0;
  if (p >= 1) return 1;
  // Solve for t given x = p, then return y(t).
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const sampleX = (t) => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t) => ((ay * t + by) * t + cy) * t;
  const dX = (t) => (3 * ax * t + 2 * bx) * t + cx;
  let t = p;
  for (let i = 0; i < 8; i++) {
    const x = sampleX(t) - p;
    if (Math.abs(x) < 1e-5) break;
    const d = dX(t);
    if (Math.abs(d) < 1e-6) break;
    t -= x / d;
  }
  return sampleY(clamp01(t));
};

/** Damped-spring-like ease that overshoots then settles at 1. */
const springEase = (p) => {
  if (p <= 0) return 0;
  if (p >= 1) return 1;
  return 1 - Math.pow(2, -10 * p) * Math.cos((p * 10 - 0.75) * ((2 * Math.PI) / 3));
};

/**
 * Apply an easing function to a 0-1 progress value.
 * Supported: linear, easeIn, easeOut, easeInOut, spring, bezier.
 */
export const ease = (p, easing, bezier) => {
  const t = clamp01(p);
  switch (easing) {
    case 'easeIn': return t * t * t;
    case 'easeOut': return 1 - Math.pow(1 - t, 3);
    case 'easeInOut': return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    case 'spring': return springEase(t);
    case 'bezier':
      if (Array.isArray(bezier) && bezier.length === 4) {
        return cubicBezierEase(t, bezier[0], bezier[1], bezier[2], bezier[3]);
      }
      return t;
    case 'linear':
    default: return t;
  }
};

// ---------------------------------------------------------------------------
// Deterministic noise (shared by jitter / scatter / drift)
// ---------------------------------------------------------------------------

/** Deterministic pseudo-random in [-1,1] from a step and seed. Mirrors worker. */
export const motionNoise = (step, seed) => {
  const x = Math.sin((step + 1) * 127.1 + seed * 311.7) * 43758.5453;
  const frac = x - Math.floor(x);
  return frac * 2 - 1;
};

/** Smooth value noise in [-1,1]: lerp between integer noise samples (for drift). */
export const smoothNoise = (x, seed) => {
  const i = Math.floor(x);
  const f = x - i;
  const u = f * f * (3 - 2 * f); // smoothstep
  const a = motionNoise(i, seed);
  const b = motionNoise(i + 1, seed);
  return a + (b - a) * u;
};

// ---------------------------------------------------------------------------
// Timing: window progress + loop + speed remap
// ---------------------------------------------------------------------------

/**
 * Compute the raw progress of a motion at absolute time `t`, honoring the
 * item window, loop mode, and an optional speed graph (time remap).
 *
 * Returns { progress (0-1), active (bool), cycle (int) }.
 */
export const computeMotionProgress = (cfg, t, itemTime) => {
  const start = (itemTime.start || 0) + Math.max(0, cfg.start || 0);
  const duration = Math.max(0.0001, cfg.duration || (itemTime.duration || 0) || 1);
  const loop = cfg.loop || { mode: 'none' };

  let local = (t - start) / duration; // in cycles (can be >1 or <0)
  if (local < 0) return { progress: 0, active: false, cycle: 0 };

  const cycle = Math.floor(local);
  let frac = local - cycle;

  const mode = loop.mode || 'none';
  const count = loop.count != null ? loop.count : Infinity;

  if (mode === 'none') {
    frac = local >= 1 ? 1 : frac;
    if (local >= 1) return applySpeed(cfg, 1, t, itemTime, 0);
    return applySpeed(cfg, frac, t, itemTime, 0);
  }

  // Looping modes:
  if (cycle >= count) {
    // Loop finished — hold final state of the last cycle.
    const endFrac = (mode === 'reverse') ? 0 : 1;
    const endCycle = count - 1;
    const finalFrac =
      mode === 'pingpong' ? ((count % 2 === 0) ? 0 : 1)
      : mode === 'reverse' ? 0
      : 1;
    return applySpeed(cfg, finalFrac, t, itemTime, endCycle);
  }

  if (mode === 'reverse') frac = 1 - frac;
  else if (mode === 'pingpong') frac = (cycle % 2 === 0) ? frac : (1 - frac);
  // mode 'repeat' leaves frac as-is.

  return applySpeed(cfg, frac, t, itemTime, cycle);
};

/** Apply an optional speed graph (piecewise-linear time remap) to a 0-1 frac. */
const applySpeed = (cfg, frac, t, itemTime, cycle) => {
  const speed = cfg.speed;
  let p = frac;
  if (speed && Array.isArray(speed.graph) && speed.graph.length >= 2) {
    p = remapByGraph(frac, speed.graph);
  }
  return { progress: clamp01(p), active: true, cycle };
};

/** Piecewise-linear remap of x in [0,1] through a graph of [t,v] points. */
const remapByGraph = (x, graph) => {
  const pts = [...graph].sort((a, b) => a[0] - b[0]);
  if (x <= pts[0][0]) return pts[0][1];
  if (x >= pts[pts.length - 1][0]) return pts[pts.length - 1][1];
  for (let i = 0; i < pts.length - 1; i++) {
    const [t0, v0] = pts[i];
    const [t1, v1] = pts[i + 1];
    if (x >= t0 && x <= t1) {
      const seg = (t1 - t0) || 1;
      return v0 + (v1 - v0) * ((x - t0) / seg);
    }
  }
  return pts[pts.length - 1][1];
};

// ---------------------------------------------------------------------------
// Keyframe interpolation (Category 7 #39) — the shared core
// ---------------------------------------------------------------------------

/**
 * Evaluate a list of keyframes at progress p (0-1).
 * keyframes: [{ t:0-1, value:number, easing?, hold?:bool, bezier? }]
 * Linear/eased interpolation between the surrounding keyframes; `hold` keeps
 * the previous value until the next keyframe time.
 */
export const evalKeyframes = (keyframes, p) => {
  if (!keyframes || keyframes.length === 0) return 0;
  const ks = [...keyframes].sort((a, b) => a.t - b.t);
  if (p <= ks[0].t) return ks[0].value;
  if (p >= ks[ks.length - 1].t) return ks[ks.length - 1].value;
  for (let i = 0; i < ks.length - 1; i++) {
    const k0 = ks[i];
    const k1 = ks[i + 1];
    if (p >= k0.t && p <= k1.t) {
      if (k0.hold) return k0.value;
      const seg = (k1.t - k0.t) || 1;
      const local = (p - k0.t) / seg;
      const e = ease(local, k0.easing || 'linear', k0.bezier);
      return k0.value + (k1.value - k0.value) * e;
    }
  }
  return ks[ks.length - 1].value;
};

// ---------------------------------------------------------------------------
// Constraints (Category 7 #44)
// ---------------------------------------------------------------------------

const applyConstraints = (result, cfg, ctx) => {
  const c = cfg.constrain;
  if (!c) return result;
  if (c.axis === 'x') result.y = 0;
  else if (c.axis === 'y') result.x = 0;
  if (c.minX != null) result.x = Math.max(result.x, c.minX);
  if (c.maxX != null) result.x = Math.min(result.x, c.maxX);
  if (c.minY != null) result.y = Math.max(result.y, c.minY);
  if (c.maxY != null) result.y = Math.min(result.y, c.maxY);
  return result;
};

// ---------------------------------------------------------------------------
// Per-unit (char/word) stagger helper (Category 3 + Category 7 #45)
// ---------------------------------------------------------------------------

/** Local eased progress for unit i of n with a stagger (seconds-ish fraction). */
export const unitProgress = (p, i, n, staggerFrac, easing, bezier) => {
  // staggerFrac is a fraction of the whole window per unit.
  const span = Math.max(0.0001, 1 - staggerFrac * (n - 1));
  const raw = (p - i * staggerFrac) / span;
  return ease(clamp01(raw), easing, bezier);
};

// ---------------------------------------------------------------------------
// Preset registry (populated by category files appended below)
// ---------------------------------------------------------------------------

const MOTION_PRESETS = {};

/** Register a motion preset compute function. fn(cfg, p, ctx, helpers) -> partial MotionResult */
export const registerMotion = (type, fn) => { MOTION_PRESETS[type] = fn; };

/** All registered motion type ids. */
export const motionTypes = () => Object.keys(MOTION_PRESETS);

export const isMotionType = (type) => Object.prototype.hasOwnProperty.call(MOTION_PRESETS, type);

// Helpers bundle passed to each preset.
const HELPERS = { ease, evalKeyframes, unitProgress, motionNoise, smoothNoise, clamp01, lerp: (a, b, t) => a + (b - a) * t };

/**
 * Main entry: compute the motion transform for an item at time t.
 */
export const computeMotion = (motionConfig, t, itemTime, ctx = {}) => {
  if (!motionConfig || !motionConfig.type) return identity();
  const fn = MOTION_PRESETS[motionConfig.type];
  if (!fn) return identity();

  const { progress, active } = computeMotionProgress(motionConfig, t, itemTime || { start: 0, duration: 1 });
  const result = identity();
  try {
    const partial = fn(motionConfig, progress, ctx, HELPERS, { active });
    if (partial && typeof partial === 'object') Object.assign(result, partial);
  } catch (e) {
    // Fail safe to identity so a bad preset never crashes the render.
    if (typeof console !== 'undefined') console.warn('motion preset error', motionConfig.type, e);
  }
  applyConstraints(result, motionConfig, ctx);
  return result;
};

// ===========================================================================
// Category 1: Basic Transform Motion
//
// `ctx` provides { width, height } so percentage/normalized units resolve
// consistently across resolutions. Position values may be given in px (default),
// '%' of the canvas, or 'norm' (0-1). resolveUnit() handles all three.
// ===========================================================================

/** Resolve a coordinate value to pixels given the unit and the axis extent. */
const resolveUnit = (value, unit, extent) => {
  if (value == null) return 0;
  if (unit === '%' || unit === 'percent') return (value / 100) * (extent || 0);
  if (unit === 'norm' || unit === 'normalized') return value * (extent || 0);
  return value; // pixels
};

// 1. Position Motion — X and Y
registerMotion('positionMotion', (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const unit = cfg.unit || 'px';
  const sx = resolveUnit(cfg.startX != null ? cfg.startX : 0, unit, ctx.width);
  const sy = resolveUnit(cfg.startY != null ? cfg.startY : 0, unit, ctx.height);
  const ex = resolveUnit(cfg.endX != null ? cfg.endX : 0, unit, ctx.width);
  const ey = resolveUnit(cfg.endY != null ? cfg.endY : 0, unit, ctx.height);
  const out = { x: h.lerp(sx, ex, e), y: h.lerp(sy, ey, e) };
  if (cfg.motionBlur) out.motionBlur = { angle: Math.atan2(ey - sy, ex - sx) * 180 / Math.PI, amount: cfg.motionBlur === true ? 6 : cfg.motionBlur };
  return out;
});

// 2. Scale Motion
registerMotion('scaleMotion', (cfg, p, ctx, h) => {
  let e = h.ease(p, cfg.easing, cfg.bezier);
  const ss = cfg.startScale != null ? cfg.startScale : 1;
  const es = cfg.endScale != null ? cfg.endScale : 1.2;
  // Optional overshoot near the end.
  const overshoot = cfg.overshoot || 0;
  const over = overshoot > 0 ? overshoot * Math.sin(Math.PI * e) : 0;
  const uniform = cfg.axis !== 'independent';
  const sxFrom = uniform ? ss : (cfg.startScaleX != null ? cfg.startScaleX : ss);
  const syFrom = uniform ? ss : (cfg.startScaleY != null ? cfg.startScaleY : ss);
  const sxTo = uniform ? es : (cfg.endScaleX != null ? cfg.endScaleX : es);
  const syTo = uniform ? es : (cfg.endScaleY != null ? cfg.endScaleY : es);
  const out = {
    scaleX: h.lerp(sxFrom, sxTo, e) + over,
    scaleY: h.lerp(syFrom, syTo, e) + over,
  };
  if (cfg.anchorX != null) out.anchorX = cfg.anchorX;
  if (cfg.anchorY != null) out.anchorY = cfg.anchorY;
  return out;
});

// 3. Rotation Motion
registerMotion('rotationMotion', (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const start = cfg.startAngle != null ? cfg.startAngle : 0;
  const end = cfg.endAngle != null ? cfg.endAngle : 360;
  const turns = cfg.turns || 0;
  const dir = cfg.direction === 'counterclockwise' ? -1 : 1;
  // Preserve full rotations — do NOT shortest-path.
  const total = (end - start) + dir * turns * 360;
  const out = { rotation: start + total * e };
  if (cfg.anchorX != null) out.anchorX = cfg.anchorX;
  if (cfg.anchorY != null) out.anchorY = cfg.anchorY;
  return out;
});

// 4. Opacity Motion
registerMotion('opacityMotion', (cfg, p, ctx, h) => {
  if (Array.isArray(cfg.keyframes) && cfg.keyframes.length) {
    return { opacity: h.clamp01(h.evalKeyframes(cfg.keyframes, p)) };
  }
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const s = cfg.startOpacity != null ? cfg.startOpacity : 0;
  const en = cfg.endOpacity != null ? cfg.endOpacity : 1;
  return { opacity: h.clamp01(h.lerp(s, en, e)) };
});

// 5. Anchor Point Motion
// Sets the pivot used for scale/rotation. If `preservePosition` is true, we
// leave position untouched (the renderer compensates); otherwise the anchor is
// simply reported for downstream transforms.
registerMotion('anchorPointMotion', (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const fromX = cfg.startAnchorX != null ? cfg.startAnchorX : 0.5;
  const fromY = cfg.startAnchorY != null ? cfg.startAnchorY : 0.5;
  const toX = cfg.endAnchorX != null ? cfg.endAnchorX : (cfg.anchorX != null ? cfg.anchorX : 0.5);
  const toY = cfg.endAnchorY != null ? cfg.endAnchorY : (cfg.anchorY != null ? cfg.anchorY : 0.5);
  return { anchorX: h.lerp(fromX, toX, e), anchorY: h.lerp(fromY, toY, e) };
});

// 6. Letter Spacing Motion
registerMotion('letterSpacingMotion', (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const s = cfg.startTracking != null ? cfg.startTracking : 20;
  const en = cfg.endTracking != null ? cfg.endTracking : 0;
  return { letterSpacing: h.lerp(s, en, e) };
});

// 7. Line Spacing Motion
registerMotion('lineSpacingMotion', (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const s = cfg.startLineSpacing != null ? cfg.startLineSpacing : 0;
  const en = cfg.endLineSpacing != null ? cfg.endLineSpacing : 0.5;
  return { lineSpacing: h.lerp(s, en, e) };
});

// ===========================================================================
// Category 2: Directional and Spatial Motion
// ===========================================================================

/** Evaluate a cubic Bézier path (array of {x,y} anchor+control points) at u in [0,1].
 *  points: [p0, c0, c1, p1, c2, c3, p2, ...] flattened as segments of 4? We use a
 *  simpler model: an array of anchor points with auto-smoothed Catmull-Rom for
 *  followPath, OR explicit cubic segments when `cubic` is true. */
const evalPolyline = (points, u) => {
  if (!points || points.length === 0) return { x: 0, y: 0 };
  if (points.length === 1) return { x: points[0].x, y: points[0].y };
  const segCount = points.length - 1;
  const scaled = u * segCount;
  const i = Math.min(segCount - 1, Math.floor(scaled));
  const local = scaled - i;
  const a = points[i];
  const b = points[i + 1];
  return { x: a.x + (b.x - a.x) * local, y: a.y + (b.y - a.y) * local };
};

/** Catmull-Rom spline through points, evaluated at u in [0,1] (smooth curve). */
const evalCatmullRom = (points, u) => {
  if (!points || points.length < 2) return evalPolyline(points, u);
  const n = points.length - 1;
  const scaled = u * n;
  const i = Math.min(n - 1, Math.floor(scaled));
  const t = scaled - i;
  const p0 = points[Math.max(0, i - 1)];
  const p1 = points[i];
  const p2 = points[i + 1];
  const p3 = points[Math.min(n, i + 2)];
  const t2 = t * t;
  const t3 = t2 * t;
  const cr = (a, b, c, d) =>
    0.5 * ((2 * b) + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
  return { x: cr(p0.x, p1.x, p2.x, p3.x), y: cr(p0.y, p1.y, p2.y, p3.y) };
};

// 8. Directional Travel — move along an angle for a distance (or to a target).
registerMotion('directionalTravel', (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const sx = cfg.startX != null ? cfg.startX : 0;
  const sy = cfg.startY != null ? cfg.startY : 0;
  let ex, ey;
  if (cfg.targetX != null || cfg.targetY != null) {
    ex = cfg.targetX != null ? cfg.targetX : sx;
    ey = cfg.targetY != null ? cfg.targetY : sy;
  } else {
    const ang = ((cfg.angle != null ? cfg.angle : 0) * Math.PI) / 180;
    const dist = cfg.distance != null ? cfg.distance : 300;
    ex = sx + Math.cos(ang) * dist;
    ey = sy + Math.sin(ang) * dist;
  }
  return { x: h.lerp(sx, ex, e), y: h.lerp(sy, ey, e) };
});

// 9. Diagonal Motion — X and Y change together.
registerMotion('diagonalMotion', (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const dx = cfg.distanceX != null ? cfg.distanceX : 300;
  const dy = cfg.distanceY != null ? cfg.distanceY : 300;
  const sx = cfg.startX != null ? cfg.startX : 0;
  const sy = cfg.startY != null ? cfg.startY : 0;
  return { x: sx + dx * e, y: sy + dy * e };
});

// 10. Zoom Travel — combine scale + position (approach/recede).
registerMotion('zoomTravel', (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const ss = cfg.startScale != null ? cfg.startScale : 0.5;
  const es = cfg.endScale != null ? cfg.endScale : 1.2;
  const dx = cfg.offsetX != null ? cfg.offsetX : 0;
  const dy = cfg.offsetY != null ? cfg.offsetY : 0;
  const s = h.lerp(ss, es, e);
  const out = { scaleX: s, scaleY: s, x: dx * e, y: dy * e };
  if (cfg.motionBlur) out.motionBlur = { angle: Math.atan2(dy, dx) * 180 / Math.PI, amount: cfg.motionBlur === true ? 6 : cfg.motionBlur };
  return out;
});

// 11. Orbit Motion — move around a center point on a circle.
registerMotion('orbitMotion', (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const cx = cfg.centerX != null ? cfg.centerX : 0;
  const cy = cfg.centerY != null ? cfg.centerY : 0;
  const radius = cfg.radius != null ? cfg.radius : 100;
  const startA = (cfg.startAngle != null ? cfg.startAngle : 0);
  const endA = (cfg.endAngle != null ? cfg.endAngle : 360);
  const turns = cfg.turns || 0;
  const dir = cfg.direction === 'counterclockwise' ? -1 : 1;
  const total = (endA - startA) + dir * turns * 360;
  const ang = ((startA + total * e) * Math.PI) / 180;
  // Position is the orbit point relative to the item's own origin (center offset).
  const out = { x: cx + Math.cos(ang) * radius, y: cy + Math.sin(ang) * radius };
  if (cfg.faceDirection) out.rotation = (startA + total * e) + 90;
  return out;
});

// 12. Circular Motion (whole-layer block mode; tangent orientation optional).
registerMotion('circularMotion', (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const radius = cfg.radius != null ? cfg.radius : 120;
  const cx = cfg.centerX != null ? cfg.centerX : 0;
  const cy = cfg.centerY != null ? cfg.centerY : 0;
  const startA = cfg.startAngle != null ? cfg.startAngle : 0;
  const endA = cfg.endAngle != null ? cfg.endAngle : 360;
  const dir = cfg.direction === 'counterclockwise' ? -1 : 1;
  const ang = ((startA + dir * (endA - startA) * e) * Math.PI) / 180;
  const out = { x: cx + Math.cos(ang) * radius, y: cy + Math.sin(ang) * radius };
  if (cfg.orientation === 'tangent') out.rotation = (startA + dir * (endA - startA) * e) + 90;
  return out;
});

// 13. Follow Path — travel along a custom curve (Catmull-Rom through points).
registerMotion('followPath', (cfg, p, ctx, h) => {
  const pts = Array.isArray(cfg.path) && cfg.path.length ? cfg.path : [{ x: 0, y: 0 }, { x: 300, y: 0 }];
  const e = h.ease(p, cfg.easing, cfg.bezier);
  let u = cfg.reverse ? 1 - e : e;
  // Optional start/end clamp along the path.
  const u0 = cfg.pathStart != null ? cfg.pathStart : 0;
  const u1 = cfg.pathEnd != null ? cfg.pathEnd : 1;
  u = u0 + (u1 - u0) * u;
  const pos = evalCatmullRom(pts, u);
  const out = { x: pos.x, y: pos.y };
  if (cfg.orient === 'path') {
    const ahead = evalCatmullRom(pts, Math.min(1, u + 0.01));
    out.rotation = Math.atan2(ahead.y - pos.y, ahead.x - pos.x) * 180 / Math.PI;
  }
  return out;
});

// 14. Motion Path with Stops — keyframed positions with holds.
// cfg.points = [{ x, y, t }], where t is 0-1 time; holds happen when two
// consecutive points share x,y across a time gap.
registerMotion('motionPathWithStops', (cfg, p, ctx, h) => {
  const pts = Array.isArray(cfg.points) && cfg.points.length ? cfg.points : [{ x: 0, y: 0, t: 0 }, { x: 300, y: 0, t: 1 }];
  const xs = pts.map((k) => ({ t: k.t, value: k.x, easing: k.easing || cfg.easing, hold: k.hold }));
  const ys = pts.map((k) => ({ t: k.t, value: k.y, easing: k.easing || cfg.easing, hold: k.hold }));
  const out = { x: h.evalKeyframes(xs, p), y: h.evalKeyframes(ys, p) };
  if (Array.isArray(cfg.rotations) && cfg.rotations.length) {
    out.rotation = h.evalKeyframes(cfg.rotations, p);
  }
  return out;
});

// ===========================================================================
// Category 3: Kinetic Typography Motion
//
// Per-char presets read ctx.text to count glyphs. Each returns a perChar array
// aligned to the rendered glyph order (whitespace included); the renderer maps
// entry i -> glyph i. Values are LOCAL offsets added on top of layout position.
// ===========================================================================

/** Number of glyphs to animate from ctx (falls back to a sensible default). */
const glyphCount = (ctx) => {
  if (ctx && typeof ctx.text === 'string' && ctx.text.length) return ctx.text.length;
  if (ctx && ctx.charCount) return ctx.charCount;
  return 1;
};

/** Build a perChar entry with identity defaults. */
const charUnit = (over) => ({ x: 0, y: 0, scaleX: 1, scaleY: 1, rotation: 0, rotationX: 0, rotationY: 0, opacity: 1, ...over });

/** Word index map from text: char index -> word index (whitespace => -1). */
const wordIndexMap = (text) => {
  const map = [];
  let word = -1, inWord = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === ' ' || ch === '\n' || ch === '\t') { map.push(-1); inWord = false; }
    else { if (!inWord) { word++; inWord = true; } map.push(word); }
  }
  return { map, wordCount: word + 1 };
};

// 15. Overshoot and Settle — move past the target then settle back (whole-item).
registerMotion('overshootSettle', (cfg, p, ctx, h) => {
  const prop = cfg.property || 'position';
  const overshoot = cfg.overshoot != null ? cfg.overshoot : 0.2; // fraction past target
  const damping = cfg.damping != null ? cfg.damping : 3;
  // Envelope reaches 1 at p=1; a decaying overshoot term adds the bounce.
  const base = h.ease(p, cfg.easing || 'easeOut', cfg.bezier);
  const decay = Math.pow(1 - p, damping);
  const osc = Math.sin(Math.PI * p) * decay; // 0 at ends, positive mid -> settles
  const amt = base + overshoot * osc;
  if (prop === 'scale') {
    const es = cfg.endScale != null ? cfg.endScale : 1;
    const ss = cfg.startScale != null ? cfg.startScale : 0.6;
    const s = ss + (es - ss) * amt;
    return { scaleX: s, scaleY: s };
  }
  if (prop === 'rotation') {
    const end = cfg.endAngle != null ? cfg.endAngle : 0;
    const start = cfg.startAngle != null ? cfg.startAngle : -30;
    return { rotation: start + (end - start) * amt };
  }
  // position (default)
  const dist = cfg.distance != null ? cfg.distance : 120;
  const dir = cfg.direction || 'up';
  const travel = dist * (1 - amt); // remaining distance to target (overshoots when amt>1)
  switch (dir) {
    case 'down': return { y: -travel };
    case 'left': return { x: travel };
    case 'right': return { x: -travel };
    case 'up':
    default: return { y: travel };
  }
});

// 16. Elastic Motion — overshoot + several oscillations (whole-item).
registerMotion('elasticMotion', (cfg, p, ctx, h) => {
  const amplitude = cfg.amplitude != null ? cfg.amplitude : 0.3;
  const frequency = cfg.frequency != null ? cfg.frequency : 3;
  const damping = cfg.damping != null ? cfg.damping : 4;
  const prop = cfg.property || 'scale';
  // Settles to 1 with decaying oscillations.
  const value = 1 - Math.cos(2 * Math.PI * frequency * p) * amplitude * Math.pow(1 - p, damping) - (1 - h.ease(p, 'easeOut'));
  const v = p >= 1 ? 1 : value;
  if (prop === 'position') {
    const dist = cfg.distance != null ? cfg.distance : 100;
    return { y: dist * (1 - v) };
  }
  if (prop === 'rotation') {
    const ang = cfg.angle != null ? cfg.angle : 20;
    return { rotation: ang * (1 - v) };
  }
  return { scaleX: v, scaleY: v };
});

// 17. Staggered Character Motion — same motion per char, time-delayed.
registerMotion('staggeredCharMotion', (cfg, p, ctx, h) => {
  const n = glyphCount(ctx);
  const stagger = cfg.stagger != null ? cfg.stagger : 0.5 / Math.max(1, n); // fraction of window per char
  const prop = cfg.property || 'position';
  const distance = cfg.distance != null ? cfg.distance : 40;
  const rand = cfg.randomness != null ? cfg.randomness : 0;
  const seed = cfg.seed != null ? cfg.seed : 1;
  const dir = cfg.direction || 'up';
  const order = cfg.order || 'forward';
  const perChar = new Array(n);
  for (let i = 0; i < n; i++) {
    const idx = order === 'reverse' ? (n - 1 - i) : (order === 'center' ? Math.abs(i - (n - 1) / 2) : i);
    let ue = h.unitProgress(p, idx, n, stagger, cfg.easing || 'easeOut', cfg.bezier);
    const rem = 1 - ue;
    const jitter = rand ? rand * h.motionNoise(i, seed) : 0;
    if (prop === 'scale') {
      const ss = cfg.startScale != null ? cfg.startScale : 0;
      perChar[i] = charUnit({ scaleX: ss + (1 - ss) * ue, scaleY: ss + (1 - ss) * ue, opacity: ue });
    } else if (prop === 'rotation') {
      const ang = cfg.angle != null ? cfg.angle : -90;
      perChar[i] = charUnit({ rotation: ang * rem, opacity: ue });
    } else {
      const d = distance * (1 + jitter);
      let x = 0, y = 0;
      switch (dir) {
        case 'down': y = -d * rem; break;
        case 'left': x = d * rem; break;
        case 'right': x = -d * rem; break;
        default: y = d * rem; break;
      }
      perChar[i] = charUnit({ x, y, opacity: ue });
    }
  }
  return { perChar };
});

// 18. Staggered Word Motion — each word moves as a group, time-delayed.
registerMotion('staggeredWordMotion', (cfg, p, ctx, h) => {
  const text = (ctx && ctx.text) || '';
  const n = text.length || 1;
  const { map, wordCount } = text ? wordIndexMap(text) : { map: [0], wordCount: 1 };
  const stagger = cfg.stagger != null ? cfg.stagger : 0.5 / Math.max(1, wordCount);
  const distance = cfg.distance != null ? cfg.distance : 60;
  const dir = cfg.direction || 'up';
  const perChar = new Array(n);
  for (let i = 0; i < n; i++) {
    const w = map[i] >= 0 ? map[i] : 0;
    const ue = h.unitProgress(p, w, wordCount, stagger, cfg.easing || 'easeOut', cfg.bezier);
    const rem = 1 - ue;
    let x = 0, y = 0;
    switch (dir) {
      case 'down': y = -distance * rem; break;
      case 'left': x = distance * rem; break;
      case 'right': x = -distance * rem; break;
      default: y = distance * rem; break;
    }
    perChar[i] = charUnit({ x, y, opacity: ue });
  }
  return { perChar };
});

// 19. Wave Motion — travelling wave across characters (loops naturally).
registerMotion('waveMotion', (cfg, p, ctx, h) => {
  const n = glyphCount(ctx);
  const amplitude = cfg.amplitude != null ? cfg.amplitude : 12;
  const cycles = cfg.cycles != null ? cfg.cycles : 2;
  const charPhase = cfg.charPhase != null ? cfg.charPhase : 0.2;
  const rotate = cfg.rotate != null ? cfg.rotate : 0;
  const mode = cfg.mode || 'vertical';
  const perChar = new Array(n);
  for (let i = 0; i < n; i++) {
    const theta = 2 * Math.PI * (cycles * p - i * charPhase);
    const s = Math.sin(theta);
    if (mode === 'rotation') perChar[i] = charUnit({ rotation: rotate ? rotate * s : amplitude * s });
    else perChar[i] = charUnit({ y: amplitude * s, rotation: rotate * s });
  }
  return { perChar };
});

// 20. Scatter and Converge — chars start displaced (deterministic), converge.
registerMotion('scatterConverge', (cfg, p, ctx, h) => {
  const n = glyphCount(ctx);
  const distance = cfg.distance != null ? cfg.distance : 150;
  const rotate = cfg.startRotation != null ? cfg.startRotation : 90;
  const startScale = cfg.startScale != null ? cfg.startScale : 1;
  const stagger = cfg.stagger != null ? cfg.stagger : 0.3 / Math.max(1, n);
  const seed = cfg.seed != null ? cfg.seed : 1;
  const reverse = !!cfg.reverse; // reverse => scatter outward
  const perChar = new Array(n);
  for (let i = 0; i < n; i++) {
    let ue = h.unitProgress(p, i, n, stagger, cfg.easing || 'easeOut', cfg.bezier);
    if (reverse) ue = 1 - ue;
    const rem = 1 - ue;
    const rx = h.motionNoise(i, seed);
    const ry = h.motionNoise(i, seed + 50);
    const rr = h.motionNoise(i, seed + 101);
    perChar[i] = charUnit({
      x: distance * rx * rem,
      y: distance * ry * rem,
      rotation: rotate * rr * rem,
      scaleX: startScale + (1 - startScale) * ue,
      scaleY: startScale + (1 - startScale) * ue,
      opacity: ue,
    });
  }
  return { perChar };
});

// 21. Tracking Expansion and Compression — animate spacing from an origin.
registerMotion('trackingExpandCompress', (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const s = cfg.startTracking != null ? cfg.startTracking : 0;
  const en = cfg.endTracking != null ? cfg.endTracking : 24;
  const spacing = h.lerp(s, en, e);
  // Report spacing; renderer applies it from the chosen origin (center/left/right).
  const out = { letterSpacing: spacing };
  if (cfg.origin) out.trackingOrigin = cfg.origin; // consumed by renderer alignment
  return out;
});

// ===========================================================================
// Category 4: 3D and Perspective Motion
//
// rotationX/rotationY are reported in degrees; the editor applies them with a
// CSS perspective() transform, the worker simulates them by squeezing the text
// along the rotated axis (cos). Depth is modeled as scale through a simple
// pinhole: projectedScale = focal / (focal + z).
// ===========================================================================

/** Project a z-depth to a scale factor via a pinhole camera. focal in px (default 800). */
const depthScale = (z, focal) => {
  const f = focal || 800;
  return f / Math.max(1, f + z);
};

// 22. 3D Rotation — rotate around X, Y, Z (Z = normal 2D rotation).
registerMotion('rotation3D', (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const fromX = cfg.startX != null ? cfg.startX : 0;
  const fromY = cfg.startY != null ? cfg.startY : 0;
  const fromZ = cfg.startZ != null ? cfg.startZ : 0;
  const toX = cfg.endX != null ? cfg.endX : 0;
  const toY = cfg.endY != null ? cfg.endY : 0;
  const toZ = cfg.endZ != null ? cfg.endZ : 0;
  const out = {
    rotationX: h.lerp(fromX, toX, e),
    rotationY: h.lerp(fromY, toY, e),
    rotation: h.lerp(fromZ, toZ, e),
  };
  if (cfg.anchorX != null) out.anchorX = cfg.anchorX;
  if (cfg.anchorY != null) out.anchorY = cfg.anchorY;
  return out;
});

// 23. Perspective Tilt — lean the text plane toward/away from the viewer.
registerMotion('perspectiveTilt', (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const hTilt = cfg.horizontalTilt != null ? cfg.horizontalTilt : 0;   // about Y
  const vTilt = cfg.verticalTilt != null ? cfg.verticalTilt : 30;      // about X
  return {
    rotationX: vTilt * e,
    rotationY: hTilt * e,
  };
});

// 24. Depth Travel — move along Z; size follows the camera projection.
registerMotion('depthTravel', (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const z0 = cfg.startZ != null ? cfg.startZ : 1200; // far
  const z1 = cfg.endZ != null ? cfg.endZ : 0;        // at camera plane
  const focal = cfg.focal != null ? cfg.focal : 800;
  const z = h.lerp(z0, z1, e);
  let s = depthScale(z, focal);
  if (cfg.minScale != null) s = Math.max(cfg.minScale, s);
  if (cfg.maxScale != null) s = Math.min(cfg.maxScale, s);
  const out = { scaleX: s, scaleY: s };
  // Fade in from the distance by default.
  if (cfg.fade !== false) out.opacity = h.clamp01(e * 1.5);
  if (cfg.motionBlur) out.motionBlur = { angle: 90, amount: cfg.motionBlur === true ? 5 : cfg.motionBlur };
  return out;
});

// 25. Parallax Motion — depth-based movement multiplier from a shared camera move.
// Simplified 2D model: closer layers (smaller depth) move more.
registerMotion('parallaxMotion', (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const camX = cfg.cameraX != null ? cfg.cameraX : 200;
  const camY = cfg.cameraY != null ? cfg.cameraY : 0;
  const depth = cfg.depth != null ? cfg.depth : 1;        // 1 = foreground, higher = further
  const intensity = cfg.intensity != null ? cfg.intensity : 1;
  const mult = intensity / Math.max(0.1, depth);
  return { x: camX * e * mult, y: camY * e * mult };
});

// 26. 3D Orbit — orbit a center in 3D; depth modulates scale; camera/path facing.
registerMotion('orbit3D', (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const cx = cfg.centerX != null ? cfg.centerX : 0;
  const cy = cfg.centerY != null ? cfg.centerY : 0;
  const rx = cfg.radiusX != null ? cfg.radiusX : 150;
  const ry = cfg.radiusY != null ? cfg.radiusY : 40; // vertical radius (perspective flattening)
  const startA = cfg.startAngle != null ? cfg.startAngle : 0;
  const turns = cfg.turns != null ? cfg.turns : 1;
  const dir = cfg.direction === 'counterclockwise' ? -1 : 1;
  const focal = cfg.focal != null ? cfg.focal : 800;
  const ang = ((startA + dir * turns * 360 * e) * Math.PI) / 180;
  // Depth: front of orbit (sin>0) is closer -> larger.
  const z = -Math.sin(ang) * (cfg.depth != null ? cfg.depth : 300);
  const s = depthScale(z, focal);
  const out = { x: cx + Math.cos(ang) * rx, y: cy + Math.sin(ang) * ry, scaleX: s, scaleY: s };
  if (cfg.facing === 'camera') { /* upright: no rotation */ }
  else if (cfg.facing === 'path') out.rotationY = Math.cos(ang) * 40;
  return out;
});

// 27. 3D Flip and Tumble — one or more full rotations around axes, settle.
registerMotion('flipTumble3D', (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const turnsX = cfg.turnsX != null ? cfg.turnsX : 0;
  const turnsY = cfg.turnsY != null ? cfg.turnsY : 1;
  const turnsZ = cfg.turnsZ != null ? cfg.turnsZ : 0;
  const finalX = cfg.finalX != null ? cfg.finalX : 0;
  const finalY = cfg.finalY != null ? cfg.finalY : 0;
  const finalZ = cfg.finalZ != null ? cfg.finalZ : 0;
  // Continuous angular trajectory that lands on the final orientation at e=1.
  return {
    rotationX: (turnsX * 360 + finalX) * e,
    rotationY: (turnsY * 360 + finalY) * e,
    rotation: (turnsZ * 360 + finalZ) * e,
  };
});

// ===========================================================================
// Category 5: Advanced Path and Layout Motion
// ===========================================================================

// 28. Text on Path — arrange each glyph along a curve (per-char).
// Each glyph is placed at a fraction along the path; rotation follows the
// tangent when `orient === 'path'`. Spacing is uniform along arc fraction
// (true kerning-aware spacing needs glyph widths from the renderer).
registerMotion('textOnPath', (cfg, p, ctx, h) => {
  const n = glyphCount(ctx);
  const pts = Array.isArray(cfg.path) && cfg.path.length ? cfg.path : [{ x: 0, y: 0 }, { x: 300, y: -60 }, { x: 600, y: 0 }];
  const u0 = cfg.pathStart != null ? cfg.pathStart : 0;
  const u1 = cfg.pathEnd != null ? cfg.pathEnd : 1;
  const spacing = cfg.spacing != null ? cfg.spacing : 1; // 0-1 fraction spread
  const reverse = !!cfg.reverse;
  const orient = cfg.orient !== false; // default true: rotate to tangent
  // Optional reveal: progress reveals glyphs left->right along the path.
  const reveal = cfg.reveal ? h.ease(p, cfg.easing, cfg.bezier) : 1;
  const perChar = new Array(n);
  for (let i = 0; i < n; i++) {
    let frac = n > 1 ? i / (n - 1) : 0;
    if (reverse) frac = 1 - frac;
    const u = u0 + (u1 - u0) * frac * spacing;
    const pos = evalCatmullRom(pts, Math.max(0, Math.min(1, u)));
    const vis = (i / Math.max(1, n)) <= reveal ? 1 : 0;
    let rot = 0;
    if (orient) {
      const ahead = evalCatmullRom(pts, Math.max(0, Math.min(1, u + 0.01)));
      rot = Math.atan2(ahead.y - pos.y, ahead.x - pos.x) * 180 / Math.PI;
    }
    // Position is reported as an ABSOLUTE per-glyph placement (renderer uses
    // perChar.x/y as the glyph origin when absolute=true).
    perChar[i] = charUnit({ x: pos.x, y: pos.y, rotation: rot, opacity: cfg.reveal ? vis : 1 });
  }
  return { perChar, perCharAbsolute: true };
});

// 29. Path Morph — travel along a path that morphs from A to B over time.
registerMotion('pathMorph', (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const a = Array.isArray(cfg.pathA) && cfg.pathA.length ? cfg.pathA : [{ x: 0, y: 0 }, { x: 300, y: 0 }];
  const b = Array.isArray(cfg.pathB) && cfg.pathB.length ? cfg.pathB : [{ x: 0, y: 0 }, { x: 300, y: -200 }];
  const morph = cfg.morph != null ? cfg.morph : e; // morph progress (defaults to time)
  // Travel progress along the (morphed) path.
  const travel = cfg.travel != null ? cfg.travel : e;
  const posA = evalCatmullRom(a, travel);
  const posB = evalCatmullRom(b, travel);
  return { x: h.lerp(posA.x, posB.x, morph), y: h.lerp(posA.y, posB.y, morph) };
});

// 30. Motion Along a Spiral — expanding/contracting spiral around a center.
registerMotion('spiralMotion', (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const cx = cfg.centerX != null ? cfg.centerX : 0;
  const cy = cfg.centerY != null ? cfg.centerY : 0;
  const r0 = cfg.startRadius != null ? cfg.startRadius : 200;
  const r1 = cfg.endRadius != null ? cfg.endRadius : 0;
  const turns = cfg.turns != null ? cfg.turns : 3;
  const dir = cfg.direction === 'counterclockwise' ? -1 : 1;
  const startA = cfg.startAngle != null ? cfg.startAngle : 0;
  const r = h.lerp(r0, r1, e);
  const ang = ((startA + dir * turns * 360 * e) * Math.PI) / 180;
  return { x: cx + Math.cos(ang) * r, y: cy + Math.sin(ang) * r };
});

// 31. Motion Along a Wave Path — travel with a sinusoidal cross-path.
registerMotion('wavePathMotion', (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const distance = cfg.distance != null ? cfg.distance : 600;
  const amplitude = cfg.amplitude != null ? cfg.amplitude : 60;
  const wavelength = cfg.wavelength != null ? cfg.wavelength : 200;
  const phase = cfg.phase != null ? cfg.phase : 0;
  const axis = cfg.axis || 'horizontal';
  const travel = distance * e;
  const cross = amplitude * Math.sin((2 * Math.PI * travel) / Math.max(1, wavelength) + phase);
  let x, y;
  if (axis === 'vertical') { x = cross; y = travel; } else { x = travel; y = cross; }
  const out = { x, y };
  if (cfg.orient === 'path') {
    // tangent slope of the wave
    const d = amplitude * (2 * Math.PI / Math.max(1, wavelength)) * Math.cos((2 * Math.PI * travel) / Math.max(1, wavelength) + phase);
    out.rotation = (axis === 'vertical' ? Math.atan2(1, d) : Math.atan2(d, 1)) * 180 / Math.PI;
  }
  return out;
});

// 32. Layout Reflow — animate glyphs from old positions to new positions.
// Real layout diffing needs the renderer's glyph metrics. The engine supports
// an explicit mapping via cfg.fromPositions / cfg.toPositions ([{x,y}] per
// glyph). Without them it is a no-op (identity) — the renderer may supply the
// positions via ctx.reflow.
registerMotion('layoutReflow', (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing, cfg.bezier);
  const from = cfg.fromPositions || (ctx && ctx.reflow && ctx.reflow.from);
  const to = cfg.toPositions || (ctx && ctx.reflow && ctx.reflow.to);
  if (!Array.isArray(from) || !Array.isArray(to)) return {};
  const n = Math.max(from.length, to.length);
  const perChar = new Array(n);
  for (let i = 0; i < n; i++) {
    const f = from[i] || to[i] || { x: 0, y: 0 };
    const tt = to[i] || from[i] || { x: 0, y: 0 };
    // Positions are deltas relative to the glyph's final layout slot.
    const fade = (f.enter || tt.exit) ? (tt.exit ? (1 - e) : e) : 1;
    perChar[i] = charUnit({ x: h.lerp(f.x || 0, tt.x || 0, e) - (tt.x || 0), y: h.lerp(f.y || 0, tt.y || 0, e) - (tt.y || 0), opacity: fade });
  }
  return { perChar };
});

// ===========================================================================
// Category 6: Organic and Expressive Motion
//
// These use elapsed SECONDS (progress * duration) for continuous/physical
// behavior so they stay deterministic during timeline scrubbing. Loops are
// seamless because they are driven by smooth periodic/noise functions.
// ===========================================================================

/** Elapsed seconds within the motion window from 0-1 progress. */
const elapsedSeconds = (cfg, p) => p * Math.max(0.0001, cfg.duration || 1);

// 33. Floating and Drifting — gentle smooth-noise drift + optional sway.
registerMotion('floatingDrifting', (cfg, p, ctx, h) => {
  const t = elapsedSeconds(cfg, p);
  const ampX = cfg.amplitudeX != null ? cfg.amplitudeX : 12;
  const ampY = cfg.amplitudeY != null ? cfg.amplitudeY : 18;
  const speed = cfg.speed != null ? cfg.speed : 0.4; // noise cycles/sec
  const seed = cfg.seed != null ? cfg.seed : 1;
  const rotate = cfg.rotate != null ? cfg.rotate : 0;
  const nx = h.smoothNoise(t * speed, seed);
  const ny = h.smoothNoise(t * speed, seed + 37);
  const nr = h.smoothNoise(t * speed * 0.7, seed + 71);
  return { x: ampX * nx, y: ampY * ny, rotation: rotate * nr };
});

// 34. Spring Motion — analytic damped spring toward a target (deterministic).
registerMotion('springMotion', (cfg, p, ctx, h) => {
  const t = elapsedSeconds(cfg, p);
  const stiffness = cfg.stiffness != null ? cfg.stiffness : 120;
  const damping = cfg.damping != null ? cfg.damping : 14;
  const mass = cfg.mass != null ? cfg.mass : 1;
  const tx = cfg.targetX != null ? cfg.targetX : 0;
  const ty = cfg.targetY != null ? cfg.targetY : 0;
  const sx = cfg.startX != null ? cfg.startX : -tx || -100;
  const sy = cfg.startY != null ? cfg.startY : -ty || 0;
  // Damped harmonic oscillator response (unit step from start->target).
  const omega0 = Math.sqrt(stiffness / mass);
  const zeta = damping / (2 * Math.sqrt(stiffness * mass));
  const resp = (x0) => {
    if (zeta < 1) {
      const wd = omega0 * Math.sqrt(1 - zeta * zeta);
      return 1 - Math.exp(-zeta * omega0 * t) * (Math.cos(wd * t) + (zeta * omega0 / wd) * Math.sin(wd * t));
    }
    // critically/over-damped: approximate with ease
    return 1 - Math.exp(-omega0 * t) * (1 + omega0 * t);
  };
  const r = Math.min(1.5, resp());
  return { x: h.lerp(sx, tx, r), y: h.lerp(sy, ty, r) };
});

// 35. Pendulum Swing — rotate around a pivot with a decaying (or steady) swing.
registerMotion('pendulumSwing', (cfg, p, ctx, h) => {
  const t = elapsedSeconds(cfg, p);
  const angle = cfg.angle != null ? cfg.angle : 20;
  const frequency = cfg.frequency != null ? cfg.frequency : 1; // Hz
  const damping = cfg.damping != null ? cfg.damping : 0;       // 0 = continuous
  const phase = cfg.phase != null ? cfg.phase : 0;
  const decay = damping > 0 ? Math.exp(-damping * t) : 1;
  const out = {
    rotation: angle * Math.cos(2 * Math.PI * frequency * t + phase) * decay,
    anchorX: cfg.anchorX != null ? cfg.anchorX : 0.5,
    anchorY: cfg.anchorY != null ? cfg.anchorY : 0, // default pivot at top edge
  };
  return out;
});

// 36. Jitter and Tremble — small irregular position/rotation shake.
registerMotion('jitterTremble', (cfg, p, ctx, h) => {
  const t = elapsedSeconds(cfg, p);
  const posAmp = cfg.positionAmplitude != null ? cfg.positionAmplitude : 3;
  const rotAmp = cfg.rotationAmplitude != null ? cfg.rotationAmplitude : 1.5;
  const frequency = cfg.frequency != null ? cfg.frequency : 18;
  const damping = cfg.damping != null ? cfg.damping : 0;
  const seed = cfg.seed != null ? cfg.seed : 1;
  const decay = damping > 0 ? Math.exp(-damping * t) : 1;
  const step = Math.floor(t * frequency);
  return {
    x: posAmp * h.motionNoise(step, seed) * decay,
    y: posAmp * h.motionNoise(step, seed + 13) * decay,
    rotation: rotAmp * h.motionNoise(step, seed + 29) * decay,
  };
});

// 37. Impulse Motion — a sudden kick at triggerTime that settles via spring.
registerMotion('impulseMotion', (cfg, p, ctx, h) => {
  const t = elapsedSeconds(cfg, p);
  const trigger = cfg.triggerTime != null ? cfg.triggerTime : 0;
  const strength = cfg.strength != null ? cfg.strength : 60;
  const damping = cfg.damping != null ? cfg.damping : 6;
  const frequency = cfg.frequency != null ? cfg.frequency : 3;
  const prop = cfg.property || 'position';
  const dir = cfg.direction || 'up';
  const dt = t - trigger;
  if (dt < 0) return {};
  // Decaying sinusoid impulse response: peaks right after trigger, settles to 0.
  const resp = strength * Math.exp(-damping * dt) * Math.sin(2 * Math.PI * frequency * dt);
  if (prop === 'scale') return { scaleX: 1 + resp / 200, scaleY: 1 + resp / 200 };
  if (prop === 'rotation') return { rotation: resp };
  switch (dir) {
    case 'down': return { y: resp };
    case 'left': return { x: -resp };
    case 'right': return { x: resp };
    default: return { y: -resp };
  }
});

// 38. Rhythmic Motion — pulse on each beat timestamp (beats in seconds).
registerMotion('rhythmicMotion', (cfg, p, ctx, h) => {
  const t = elapsedSeconds(cfg, p);
  const beats = Array.isArray(cfg.beats) && cfg.beats.length ? cfg.beats : [0, 0.5, 1, 1.5, 2];
  const intensity = cfg.intensity != null ? cfg.intensity : 0.2;
  const decay = cfg.decay != null ? cfg.decay : 8;
  const prop = cfg.property || 'scale';
  const smoothing = cfg.smoothing != null ? cfg.smoothing : 1;
  // Find the most recent beat at or before t.
  let last = -Infinity;
  for (let i = 0; i < beats.length; i++) { if (beats[i] <= t && beats[i] > last) last = beats[i]; }
  if (!isFinite(last)) return {};
  const dt = t - last;
  const env = Math.exp(-decay / Math.max(0.1, smoothing) * dt); // 1 at beat -> 0
  const pulse = intensity * env;
  if (prop === 'position') return { y: -pulse * 40 };
  if (prop === 'rotation') return { rotation: pulse * 10 };
  if (prop === 'tracking') return { letterSpacing: pulse * 20 };
  return { scaleX: 1 + pulse, scaleY: 1 + pulse };
});

// ===========================================================================
// Category 7: Advanced Motion Controls
//
// Several of these are core features exposed as selectable "types":
//  - speedGraph / loopControls / constraints only configure the shared core
//    (cfg.speed / cfg.loop / cfg.constrain). Their preset fns just drive a base
//    position via keyframes (or identity) and let the core do the work.
//  - parentLinked / presets are handled at the UI/render composition layer;
//    the engine returns identity so they never break playback.
// ===========================================================================

// 39. Keyframe Interpolation — per-property keyframe tracks.
// cfg.tracks = { x:[{t,value,easing,hold,bezier}], y:[...], scale:[...],
//                rotation:[...], opacity:[...], letterSpacing:[...] }
registerMotion('keyframeInterpolation', (cfg, p, ctx, h) => {
  const tracks = cfg.tracks || {};
  const out = {};
  if (Array.isArray(tracks.x)) out.x = h.evalKeyframes(tracks.x, p);
  if (Array.isArray(tracks.y)) out.y = h.evalKeyframes(tracks.y, p);
  if (Array.isArray(tracks.scale)) { const s = h.evalKeyframes(tracks.scale, p); out.scaleX = s; out.scaleY = s; }
  if (Array.isArray(tracks.scaleX)) out.scaleX = h.evalKeyframes(tracks.scaleX, p);
  if (Array.isArray(tracks.scaleY)) out.scaleY = h.evalKeyframes(tracks.scaleY, p);
  if (Array.isArray(tracks.rotation)) out.rotation = h.evalKeyframes(tracks.rotation, p);
  if (Array.isArray(tracks.opacity)) out.opacity = h.clamp01(h.evalKeyframes(tracks.opacity, p));
  if (Array.isArray(tracks.letterSpacing)) out.letterSpacing = h.evalKeyframes(tracks.letterSpacing, p);
  return out;
});

// 40. Speed Graph / Velocity Control — the shared speed remap already runs in
// the core (cfg.speed.graph). This preset drives a base position path by
// keyframes (or endX/Y) so the remapped progress is visible.
registerMotion('speedGraph', (cfg, p, ctx, h) => {
  // p here is ALREADY remapped by the core if cfg.speed is present.
  const sx = cfg.startX != null ? cfg.startX : 0;
  const sy = cfg.startY != null ? cfg.startY : 0;
  const ex = cfg.endX != null ? cfg.endX : 300;
  const ey = cfg.endY != null ? cfg.endY : 0;
  return { x: h.lerp(sx, ex, p), y: h.lerp(sy, ey, p) };
});

// 41. Motion Blur — standalone directional blur descriptor (no transform).
registerMotion('motionBlur', (cfg, p, ctx, h) => {
  const amount = cfg.amount != null ? cfg.amount : 8;
  const angle = cfg.angle != null ? cfg.angle : 0;
  return { motionBlur: { angle, amount }, blur: cfg.isotropic ? amount : 0 };
});

// 42. Parent and Linked Motion — composition is handled by the renderer's
// transform hierarchy. The engine contributes identity; the renderer composes
// cfg.parentId's transform with this item's local transform.
registerMotion('parentLinked', () => ({}));

// 43. Motion Loop Controls — loop mode is applied by the core
// (computeMotionProgress). This preset exposes it as a type and drives a simple
// base oscillation path so the loop is visible.
registerMotion('loopControls', (cfg, p, ctx, h) => {
  const sx = cfg.startX != null ? cfg.startX : 0;
  const sy = cfg.startY != null ? cfg.startY : 0;
  const ex = cfg.endX != null ? cfg.endX : 0;
  const ey = cfg.endY != null ? cfg.endY : -60;
  const e = h.ease(p, cfg.easing, cfg.bezier);
  return { x: h.lerp(sx, ex, e), y: h.lerp(sy, ey, e) };
});

// 44. Motion Constraints — applied by the core applyConstraints(cfg.constrain)
// AFTER the preset runs. This preset drives a base position that the constraint
// then clamps.
registerMotion('constraints', (cfg, p, ctx, h) => {
  const sx = cfg.startX != null ? cfg.startX : 0;
  const sy = cfg.startY != null ? cfg.startY : 0;
  const ex = cfg.endX != null ? cfg.endX : 400;
  const ey = cfg.endY != null ? cfg.endY : 0;
  const e = h.ease(p, cfg.easing, cfg.bezier);
  return { x: h.lerp(sx, ex, e), y: h.lerp(sy, ey, e) };
});

// 45. Per-Character Motion Controls — explicit per-glyph transforms + stagger.
// cfg.chars = [{ index, x, y, scale, rotation, opacity, delay }]  (delay in
// fraction of window). Unlisted glyphs stay at identity. cfg.stagger applies a
// uniform per-index delay when per-char `delay` is not given.
registerMotion('perCharacterControls', (cfg, p, ctx, h) => {
  const n = glyphCount(ctx);
  const chars = Array.isArray(cfg.chars) ? cfg.chars : [];
  const byIndex = {};
  chars.forEach((c) => { if (c && c.index != null) byIndex[c.index] = c; });
  const stagger = cfg.stagger != null ? cfg.stagger : 0;
  const perChar = new Array(n);
  for (let i = 0; i < n; i++) {
    const c = byIndex[i];
    if (!c) { perChar[i] = charUnit({}); continue; }
    const delay = c.delay != null ? c.delay : stagger * i;
    const local = h.clamp01((p - delay) / Math.max(0.0001, 1 - delay));
    const e = h.ease(local, c.easing || cfg.easing, cfg.bezier);
    perChar[i] = charUnit({
      x: (c.x || 0) * (1 - e),         // ease from offset toward 0 (settle to layout)
      y: (c.y || 0) * (1 - e),
      rotation: (c.rotation || 0) * (1 - e),
      scaleX: c.scale != null ? h.lerp(c.scale, 1, e) : 1,
      scaleY: c.scale != null ? h.lerp(c.scale, 1, e) : 1,
      opacity: c.opacity != null ? h.lerp(c.opacity, 1, e) : (c.fadeIn ? e : 1),
    });
  }
  return { perChar };
});

// 46. Motion Presets and Combinations — a preset is expanded into a concrete
// motion config at authoring time (in the UI). At runtime a 'preset' type with
// no expansion is a no-op; if cfg.resolvedType is present we delegate to it.
registerMotion('presets', (cfg, p, ctx, h) => {
  // If the UI stored a resolved base motion, run it; otherwise identity.
  if (cfg.resolvedType && isMotionType(cfg.resolvedType) && cfg.resolvedType !== 'presets') {
    // Re-dispatch using the resolved params merged over cfg.
    const inner = { ...cfg, ...(cfg.resolvedParams || {}), type: cfg.resolvedType };
    return computeMotion(inner, (ctx && ctx._absTime) || 0, (ctx && ctx._itemTime) || { start: 0, duration: cfg.duration || 1 }, ctx);
  }
  return {};
});

// ===========================================================================
// Category 8: Cinematic Camera Motion (image-focused Ken Burns & camera moves)
//
// These produce a uniform scale (>= 1 so panning never reveals empty edges)
// plus an x/y pan offset that moves a normalized focal point (0..1) toward the
// frame center. Returned via MotionResult.scaleX/scaleY + x/y. ctx.width/height
// scale the pan so it is resolution-independent. Reused by image items (the
// renderers already apply motion to images), and usable on any layer.
//
// cameraMove is the general preset; the named presets are sugar over it.
// cfg: { startScale, endScale, startFocusX, startFocusY, endFocusX, endFocusY,
//        easing }  (focus values normalized 0..1, default center 0.5)
// ===========================================================================

/** Shared camera-move math: scale + pan that keeps a focal point framed. */
const cameraMoveCore = (cfg, p, ctx, h) => {
  const e = h.ease(p, cfg.easing || 'easeInOut', cfg.bezier);
  const w = (ctx && ctx.width) || 0;
  const hgt = (ctx && ctx.height) || 0;
  const s0 = cfg.startScale != null ? cfg.startScale : 1;
  const s1 = cfg.endScale != null ? cfg.endScale : 1.25;
  const scale = Math.max(1, h.lerp(s0, s1, e)); // never below 1 (fit-safe)
  const fx0 = cfg.startFocusX != null ? cfg.startFocusX : 0.5;
  const fy0 = cfg.startFocusY != null ? cfg.startFocusY : 0.5;
  const fx1 = cfg.endFocusX != null ? cfg.endFocusX : fx0;
  const fy1 = cfg.endFocusY != null ? cfg.endFocusY : fy0;
  const fx = h.lerp(fx0, fx1, e);
  const fy = h.lerp(fy0, fy1, e);
  // Shift so the focal point lands at the box center, scaled by the zoom.
  // Clamp the pan so the scaled image still covers the box (no empty edges).
  const maxPanX = Math.max(0, (scale - 1) * w) / 2;
  const maxPanY = Math.max(0, (scale - 1) * hgt) / 2;
  let x = (0.5 - fx) * w * scale;
  let y = (0.5 - fy) * hgt * scale;
  x = Math.max(-maxPanX, Math.min(maxPanX, x));
  y = Math.max(-maxPanY, Math.min(maxPanY, y));
  return { x, y, scaleX: scale, scaleY: scale, anchorX: 0.5, anchorY: 0.5 };
};

// 47. General camera move (focal point + zoom).
registerMotion('cameraMove', (cfg, p, ctx, h) => cameraMoveCore(cfg, p, ctx, h));

// 48. Ken Burns Zoom In — slow push toward a focal point.
registerMotion('kenBurnsZoomIn', (cfg, p, ctx, h) => cameraMoveCore({
  ...cfg,
  startScale: cfg.startScale != null ? cfg.startScale : 1.0,
  endScale: cfg.endScale != null ? cfg.endScale : 1.3,
  startFocusX: cfg.startFocusX != null ? cfg.startFocusX : 0.5,
  startFocusY: cfg.startFocusY != null ? cfg.startFocusY : 0.5,
  endFocusX: cfg.focusX != null ? cfg.focusX : (cfg.endFocusX != null ? cfg.endFocusX : 0.5),
  endFocusY: cfg.focusY != null ? cfg.focusY : (cfg.endFocusY != null ? cfg.endFocusY : 0.5),
  easing: cfg.easing || 'easeInOut',
}, p, ctx, h));

// 49. Ken Burns Zoom Out — start close, pull back to reveal.
registerMotion('kenBurnsZoomOut', (cfg, p, ctx, h) => cameraMoveCore({
  ...cfg,
  startScale: cfg.startScale != null ? cfg.startScale : 1.3,
  endScale: cfg.endScale != null ? cfg.endScale : 1.0,
  startFocusX: cfg.focusX != null ? cfg.focusX : (cfg.startFocusX != null ? cfg.startFocusX : 0.5),
  startFocusY: cfg.focusY != null ? cfg.focusY : (cfg.startFocusY != null ? cfg.startFocusY : 0.5),
  endFocusX: cfg.endFocusX != null ? cfg.endFocusX : 0.5,
  endFocusY: cfg.endFocusY != null ? cfg.endFocusY : 0.5,
  easing: cfg.easing || 'easeInOut',
}, p, ctx, h));

// 50. Pan Left — sweep across the image to the left (focus moves right->left).
registerMotion('panLeft', (cfg, p, ctx, h) => cameraMoveCore({
  ...cfg, startScale: cfg.scale || 1.25, endScale: cfg.scale || 1.25,
  startFocusX: 0.7, endFocusX: 0.3, startFocusY: 0.5, endFocusY: 0.5, easing: cfg.easing || 'linear',
}, p, ctx, h));

// 51. Pan Right.
registerMotion('panRight', (cfg, p, ctx, h) => cameraMoveCore({
  ...cfg, startScale: cfg.scale || 1.25, endScale: cfg.scale || 1.25,
  startFocusX: 0.3, endFocusX: 0.7, startFocusY: 0.5, endFocusY: 0.5, easing: cfg.easing || 'linear',
}, p, ctx, h));

// 52. Tilt Up — frame moves up through the photo (focus moves down->up).
registerMotion('tiltUp', (cfg, p, ctx, h) => cameraMoveCore({
  ...cfg, startScale: cfg.scale || 1.25, endScale: cfg.scale || 1.25,
  startFocusY: 0.7, endFocusY: 0.3, startFocusX: 0.5, endFocusX: 0.5, easing: cfg.easing || 'linear',
}, p, ctx, h));

// 53. Tilt Down.
registerMotion('tiltDown', (cfg, p, ctx, h) => cameraMoveCore({
  ...cfg, startScale: cfg.scale || 1.25, endScale: cfg.scale || 1.25,
  startFocusY: 0.3, endFocusY: 0.7, startFocusX: 0.5, endFocusX: 0.5, easing: cfg.easing || 'linear',
}, p, ctx, h));

// 54. Dolly In — camera moves closer (centered zoom in).
registerMotion('dollyIn', (cfg, p, ctx, h) => cameraMoveCore({
  ...cfg, startScale: 1.0, endScale: cfg.endScale != null ? cfg.endScale : 1.4,
  startFocusX: 0.5, endFocusX: 0.5, startFocusY: 0.5, endFocusY: 0.5, easing: cfg.easing || 'easeInOut',
}, p, ctx, h));

// 55. Dolly Out — camera pulls back (centered zoom out).
registerMotion('dollyOut', (cfg, p, ctx, h) => cameraMoveCore({
  ...cfg, startScale: cfg.startScale != null ? cfg.startScale : 1.4, endScale: 1.0,
  startFocusX: 0.5, endFocusX: 0.5, startFocusY: 0.5, endFocusY: 0.5, easing: cfg.easing || 'easeInOut',
}, p, ctx, h));

// 56. Pan and Zoom — diagonal move with a push (classic Ken Burns combo).
registerMotion('panAndZoom', (cfg, p, ctx, h) => cameraMoveCore({
  ...cfg,
  startScale: cfg.startScale != null ? cfg.startScale : 1.05,
  endScale: cfg.endScale != null ? cfg.endScale : 1.35,
  startFocusX: cfg.startFocusX != null ? cfg.startFocusX : 0.35,
  startFocusY: cfg.startFocusY != null ? cfg.startFocusY : 0.4,
  endFocusX: cfg.endFocusX != null ? cfg.endFocusX : 0.65,
  endFocusY: cfg.endFocusY != null ? cfg.endFocusY : 0.6,
  easing: cfg.easing || 'easeInOut',
}, p, ctx, h));
